import React, { type JSX } from "react";

import { getOptionValueByIdx } from "../apps/configurator/utils";
import { getProductVariantsForOptionCodes } from "../apps/selectors";
import { ConfiguratorTypes } from "../constants";
import {
    IOptionCode,
    IOptionSingleValue,
    IOptionValues,
    IProduct,
} from "../models/catalogue.interfaces";
import { range } from "../utils/functional";
import { ProductOptionSelector } from "./ProductOptionSelector";

interface IProps {
    selectorID: string;
    rootProduct: IProduct | null;
    variant: IProduct | null;
    code: IOptionCode;
    values: IOptionValues;
    onOptionChange: (
        namespace: string,
        code: IOptionCode,
        index: number,
        totalNumValues: number,
        value: IOptionSingleValue,
    ) => void;
    configuratorType?: ConfiguratorTypes;
    style?: "full" | "mini";
    chatLinkComponent?: JSX.Element | null;
    liveChatHeader?: string;
    enableChat?: boolean;
    isPlpPanelStyle?: boolean;
}

const getSelectorCount = (
    rootProduct: IProduct | null,
    code: IOptionCode,
    values: IOptionValues,
): number => {
    if (!rootProduct) {
        return 0;
    }
    // Get the codes which are before this selector in the drill-down precedence
    const allCodes = rootProduct.attributes.product_options?.value || [];
    const prevCodes = allCodes.slice(0, allCodes.indexOf(code));
    // Filter the variants down to those possible for the previous selectors
    const variants =
        prevCodes.length > 0
            ? getProductVariantsForOptionCodes([rootProduct], prevCodes, values)
            : rootProduct.children;
    // Figure out how many selectors to show from the filtered variant list
    // Example of goal: show 1 option_feel when option_size is Queen, but 2 option_size is Split King.
    const maxValueCount = variants.reduce<number>((memo, variant) => {
        const val = variant.attributes[code]?.value || [];
        const len = Array.isArray(val) ? val.length : 1;
        return Math.max(len, memo);
    }, 0);
    // As long as there's a root product, always show at least 1 selector
    return Math.max(maxValueCount, 1);
};

export const ProductOptionSelectorSet = React.memo(
    (props: IProps): React.ReactNode => {
        const numSelectors = getSelectorCount(
            props.rootProduct,
            props.code,
            props.values,
        );
        return range(numSelectors).map((num) => {
            const i = num - 1;
            return (
                <ProductOptionSelector
                    key={i}
                    {...props}
                    selectorID={`${props.selectorID}-${i}`}
                    value={getOptionValueByIdx(props.values[props.code], i)}
                    onOptionChange={(namespace, code, value) => {
                        props.onOptionChange(
                            namespace,
                            code,
                            i,
                            numSelectors,
                            value,
                        );
                    }}
                />
            );
        });
    },
);
